﻿import { isMapResultsFetching, selectMapLibraryStatus } from 'features/searchResultsMap';
import { selectUiState } from 'layout/redux/selectors';
import { store, store$ } from '@redux/store';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  fromEvent,
  map,
  merge,
  pairwise,
  skip,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import LeadFormType from 'features/leadGenerators';
import {
  selectIsInProgress,
  speedCurveMarkEnd,
  speedCurveMarkStart,
  speedCurveTrackConversion,
  speedcurveStateActions,
  speedcurveTimingMetric,
} from 'features/speedcurve';
import LoadingStatus from 'enums/loadingStatus';
import {
  UrlSubdirectoryTypes,
  selectFacts,
  selectFeatures,
  selectFetchResultListingsStatus,
  selectLocation,
  selectResultsModifiers,
} from 'features/searchResultsFacets';
import { selectRoutingState } from 'features/routing';

const isLuxDefined = () => !!window.LUX;

const startSpeedcurveTracking = () => {
  const state = store.getState();
  const isInProgress = selectIsInProgress(state);
  // Speedcurve is in progress and make interaction in the DOM fast, we need to end and start again the tracking.
  if (isInProgress) {
    const routing = selectRoutingState(state);
    window.LUX.label = UrlSubdirectoryTypes.fromKey(routing.urlSubdirectory).luxLabel;
    window.LUX.send();
    window.LUX.init();
    return;
  }
  store.dispatch(speedcurveStateActions.start());
};
const endSpeedcurveTracking = () => store.dispatch(speedcurveStateActions.end());

const paidModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => LeadFormType.InformationFormBasic.toString() !== modalInformation.leadGeneratorOpener)
);

const trackPaidFormSend = () => store.dispatch(speedCurveTrackConversion([{ name: 'lead_sent', value: 1 }]));

paidModalSubmission$.subscribe(trackPaidFormSend);

const onMapLibraryStatusChange$ = store$.pipe(map(selectMapLibraryStatus), distinctUntilChanged());

const trackMapLibraryStart = () => store.dispatch(speedCurveMarkStart([{ name: 'map_script_start' }]));

const trackMapLibraryLoaded = () =>
  store.dispatch(
    speedCurveMarkEnd([
      {
        measureName: 'map_downloaded',
        markNameStart: 'map_script_start',
        markNameEnd: 'map_script_end',
      },
    ])
  );

onMapLibraryStatusChange$
  .pipe(filter((status) => status === LoadingStatus.Loading.toString()))
  .subscribe(trackMapLibraryStart);

onMapLibraryStatusChange$
  .pipe(filter((status) => status === LoadingStatus.Succeeded.toString()))
  .subscribe(trackMapLibraryLoaded);

const onMapResultsChange$ = store$.pipe(map(isMapResultsFetching), distinctUntilChanged());

const trackMapResultsStart = () => store.dispatch(speedCurveMarkStart([{ name: 'map_points_start' }]));

const trackMapResultsLoaded = () =>
  store.dispatch(
    speedCurveMarkEnd([
      {
        measureName: 'map_points_request',
        markNameStart: 'map_points_start',
        markNameEnd: 'map_points_end',
      },
    ])
  );

onMapResultsChange$.pipe(filter(Boolean)).subscribe(trackMapResultsStart);

onMapResultsChange$
  .pipe(
    filter((isFetching) => !isFetching),
    skip(1)
  )
  .subscribe(trackMapResultsLoaded);

/**
 * The `mapResources` function maps script, link, and font resources on a web page to their
 * corresponding URLs.
 * @returns The function `mapResources` returns an object that contains the resources mapped by their
 * respective URLs or values. The object includes three properties: `scripts`, `links`, and `fonts`.
 * Each property is an object that maps the URL or value of the resource to its corresponding
 * `data-resource` attribute value.
 */
const mapResources = () => {
  const scriptResources = document.querySelectorAll('script[data-resource]');
  const linkResources = document.querySelectorAll('link[data-resource]');
  const fontResources = document.querySelectorAll('[data-resource="font"]');
  const scripts = {};
  const links = {};
  const fonts = {};

  scriptResources.forEach((resource) => {
    scripts[resource.src] = resource.dataset.resource;
  });

  linkResources.forEach((resource) => {
    links[resource.href] = resource.dataset.resource;
  });

  fontResources.forEach((resource) => {
    fonts[resource.value] = resource.dataset.resource;
  });

  return { ...scripts, ...links, ...fonts };
};

/**
 * The function `trackResourceTiming` retrieves performance timing data for specified resources and
 * dispatches an action with the data.
 * @param resources - The `resources` parameter is an object that maps resource URLs to their
 * corresponding keys. The keys are used to identify the resources in the `speedcurveResources` array.
 */
const trackResourceTiming = (resources) => {
  const entries = performance?.getEntriesByType('resource') || [];
  const speedcurveResources = [];
  entries.forEach((entry) => {
    const key = resources[entry.name];
    if (key) {
      speedcurveResources.push({
        name: key,
        value: entry.duration,
      });
    }
  });

  if (speedcurveResources.length) {
    store.dispatch(speedcurveTimingMetric(speedcurveResources));
  }
};

fromEvent(window, 'load').pipe(map(mapResources)).subscribe(trackResourceTiming);

const getKeyAd = (elementId) => {
  const key = elementId.split('--');
  return key[key.length - 1];
};

const trackAdRequestStart = () => store.dispatch(speedCurveMarkStart([{ name: `ad_request_start` }]));

fromEvent(window, 'adRequestStart').subscribe(trackAdRequestStart);

const trackAdRequestEnd = (key) =>
  store.dispatch(
    speedCurveMarkEnd([
      {
        measureName: `ad_${key}_measure`,
        markNameStart: `ad_request_start`,
        markNameEnd: `ad_${key}_end`,
      },
    ])
  );

fromEvent(window, 'adRequestEnd')
  .pipe(map((event) => getKeyAd(event.detail.elementId)))
  .subscribe(trackAdRequestEnd);

const trackAdScriptStart = () => store.dispatch(speedCurveMarkStart([{ name: `ads_script_start` }]));

fromEvent(window, 'adScriptStart').subscribe(trackAdScriptStart);

const trackAdScriptEnd = () =>
  store.dispatch(
    speedCurveMarkEnd([
      {
        measureName: `ads_script_measure`,
        markNameStart: `ads_script_start`,
        markNameEnd: `ads_script_end`,
      },
    ])
  );

fromEvent(window, 'adScriptEnd').subscribe(trackAdScriptEnd);

const trackAdParametersStart = () => store.dispatch(speedCurveMarkStart([{ name: `ads_parameters_definition_start` }]));

fromEvent(window, 'adParametersStart').subscribe(trackAdParametersStart);

const trackAdParametersEnd = () =>
  store.dispatch(
    speedCurveMarkEnd([
      {
        measureName: `ads_parameters_definition_measure`,
        markNameStart: `ads_parameters_definition_start`,
        markNameEnd: `ads_parameters_definition_end`,
      },
    ])
  );

fromEvent(window, 'adParametersEnd').subscribe(trackAdParametersEnd);

/* Measure list view interactions */

/**
 * Dispatches a `speedCurveMarkStart` action to mark when the interaction start.
 */
const markStartEvent = ({ markNameStart }) => store.dispatch(speedCurveMarkStart([{ name: markNameStart }]));

/**
 * Dispatches an action to mark the end of a performance measurement for the list view being loaded.
 */
const trackListViewLoaded = ({ markNameStart, measureName }) =>
  store.dispatch(
    speedCurveMarkEnd([
      {
        measureName,
        markNameStart,
        markNameEnd: `list_view_loaded`,
      },
    ])
  );

const tabContainer = document.querySelectorAll('[data-result-type-bar]');

/** Listen click events on elements tab results.
 */
const tabsClicks$ = fromEvent(tabContainer, 'click').pipe(tap(startSpeedcurveTracking));

/**
 * Creates an observable that emit a value when results are succeded.
 * We add the pairwise because after the fetchResultListingsStatus is set to @LoadingStatus.Succeeded,
 * in the next iteraction the fetchResultListingsStatus is Succeded and it doesn't change to Loading.
 * For this reason we need to check if the last value was Loading and the current value is Succeded.
 */
const listViewLoaded$ = store$.pipe(
  map(selectFetchResultListingsStatus),
  distinctUntilChanged(),
  pairwise(),
  filter(([previous, current]) => previous === LoadingStatus.Loading && current === LoadingStatus.Succeeded)
);

/** Observable to track the interaction when a user clicks on
 * tabs and measure how much time takes the list view to be ready.
 */
const measureListViewOnTabsInteraction$ = tabsClicks$.pipe(
  map(() => ({
    markNameStart: 'tabs_click',
  })),
  tap(markStartEvent),
  switchMap(({ markNameStart }) =>
    listViewLoaded$.pipe(
      tap(() =>
        trackListViewLoaded({
          markNameStart,
          measureName: `tabs_interaction_list_view`,
        })
      ),
      take(1)
    )
  )
);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in facts leaf change.
 */
const facts$ = store$.pipe(map(selectFacts), filter(Boolean), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in features leaf change.
 */
const features$ = store$.pipe(map(selectFeatures), filter(Boolean), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in results modifiers leaf change.
 */
const resultsModifiers$ = store$.pipe(map(selectResultsModifiers), filter(Boolean), distinctUntilChanged());

const onFilterChange$ = combineLatest([facts$, features$, resultsModifiers$]).pipe(
  debounceTime(100),
  skip(1),
  tap(startSpeedcurveTracking)
);

/** Observable to track the interaction when a filter is applied
 *  and measure how much time takes the list view to be ready.
 */
const measureListViewOnFiltersIteraction$ = onFilterChange$.pipe(
  map(() => ({
    markNameStart: 'filter_applied',
  })),
  tap(markStartEvent),
  switchMap(({ markNameStart }) =>
    listViewLoaded$.pipe(
      tap(() =>
        trackListViewLoaded({
          markNameStart,
          measureName: `filter_interaction_list_view`,
        })
      ),
      take(1)
    )
  )
);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in location leaf change, fetch the new results for map.
 */
const locationChange$ = store$.pipe(
  map(selectLocation),
  filter(Boolean),
  distinctUntilChanged(),
  skip(1),
  tap(startSpeedcurveTracking)
);

/** Observable to track the interaction when a user change the location
 * and measure how much time takes the list view to be ready.
 */
const measureListViewOnLocationIteraction$ = locationChange$.pipe(
  map(() => ({
    markNameStart: 'location_change',
    measureName: `location_interaction_list_view`,
  })),
  tap(markStartEvent),
  switchMap((measureNames) =>
    listViewLoaded$.pipe(
      tap(() => trackListViewLoaded(measureNames)),
      take(1)
    )
  )
);

merge(
  measureListViewOnTabsInteraction$,
  measureListViewOnFiltersIteraction$,
  measureListViewOnLocationIteraction$
).subscribe();

/** Init LUX  */
const pathname$ = store$.pipe(map(selectRoutingState), filter(Boolean), distinctUntilKeyChanged('currentUrl'));
/**
 * The function `markLuxLoadTime` calls the `markLoadTime` method of the `LUX` object in the
 * `window` scope.
 */
const markLuxLoadTime = () => window.LUX.markLoadTime();

const onSpeedcurveIsInProgress$ = store$.pipe(
  filter(isLuxDefined),
  map(selectIsInProgress),
  distinctUntilChanged(),
  filter(Boolean)
);

onSpeedcurveIsInProgress$.subscribe(() => window.LUX.init());

const onPageLoadedOnAjax$ = store$.pipe(
  map((state) => {
    const fetchResultListingStatus = selectFetchResultListingsStatus(state);
    return fetchResultListingStatus === LoadingStatus.Succeeded && state?.mapState?.isFetchingResults === false;
  }),
  distinctUntilChanged()
);

const onResultsLoaded$ = store$.pipe(
  filter(isLuxDefined),
  map((state) => {
    const fetchResultListingStatus = selectFetchResultListingsStatus(state);
    return fetchResultListingStatus === LoadingStatus.Loading || state?.mapState?.isFetchingResults;
  }),
  distinctUntilChanged(),
  map((isFetching) => isFetching === false),
  filter(Boolean),
  skip(1),
  tap(markLuxLoadTime),
  switchMap(() => onPageLoadedOnAjax$.pipe(filter(Boolean), take(1))),
  withLatestFrom(pathname$),
  map(([, routing]) => routing)
);

onResultsLoaded$
  .pipe(
    // Speedcurve recommendation, Force SpeedCurve RUM to capture data for a minimum amount
    // of time before sending the beacon. Equivalent to minMeasureTime.
    debounceTime(5000)
  )
  .subscribe((routing) => {
    window.LUX.label = UrlSubdirectoryTypes.fromKey(routing.urlSubdirectory).luxLabel;
    window.LUX.send();
    endSpeedcurveTracking();
  });
